import {
  type Table,
  type TableFeature,
  type Updater,
  functionalUpdate,
} from "@tanstack/react-table";

// Use declaration merging to add our new feature APIs and state types to TanStack Table's existing types.
declare module "@tanstack/react-table" {
  interface TableState {
    hoveredRowId?: string | null;
    hoveredColId?: string | null;
  }
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  interface Table<TData> {
    setHoveredRowId: (updater: Updater<string | null | undefined>) => void;
    setHoveredColId: (updater: Updater<string | null | undefined>) => void;
  }
}

type HoverState = {
  hoveredRowId?: string | null;
  hoveredColId?: string | null;
};

/**
 * HoverFeature: TanStack Table v8用のカスタム機能。
 * 行・列ホバー状態（hoveredRowId, hoveredColId）をtableインスタンスで一元管理できる。
 *
 * - table.getState().hoveredRowId, hoveredColId で現在のホバー状態を取得
 * - table.setHoveredRowId, table.setHoveredColId で状態を更新
 * - DataTable描画時やColumnDefのcell/header関数内でtable.getState()を参照することで、
 *   hover状態をColumnDefレベルで柔軟に利用できる
 *
 * 各Cellでホバー状態を更新するには、getHoverCellProps をインポートし、metaに以下のように記載する:
 *
 * ```tsx
 * import { getHoverCellProps } from "@/components/DataTable/Features/hoverFeature";
 *
 * // ...
 * meta: {
 *   getCellProps: (cell, table) => getHoverCellProps(cell, table),
 *   // 他の meta プロパティ...
 * }
 * ```
 *
 * @see https://tanstack.com/table/latest/docs/guide/custom-features
 */
export const HoverFeature: TableFeature = {
  getInitialState: (initialState): HoverState => ({
    ...initialState,
    hoveredRowId: null,
    hoveredColId: null,
  }),

  createTable: function <TData>(table: Table<TData>): void {
    table.setHoveredRowId = (updater: Updater<string | null | undefined>) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
      table.options.onStateChange?.((old: any) => ({
        ...old,
        hoveredRowId: functionalUpdate(updater, old.hoveredRowId),
      }));
    };

    table.setHoveredColId = (updater: Updater<string | null | undefined>) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
      table.options.onStateChange?.((old: any) => ({
        ...old,
        hoveredColId: functionalUpdate(updater, old.hoveredColId),
      }));
    };
  },
};

export function getHoverCellProps(
  cell: { row: { id: string }; column: { id: string } },
  table: {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    setHoveredRowId?: (updater: any) => void;
    // biome-ignore lint/suspicious/noExplicitAny: <explanation> 型絞りたいけど、調査できてないので一旦ignore
    setHoveredColId?: (updater: any) => void;
  },
) {
  const rowId = cell.row.id;
  const colId = cell.column.id;

  return {
    onMouseEnter: () => {
      if (rowId !== undefined) table.setHoveredRowId?.(rowId);
      if (colId !== undefined) table.setHoveredColId?.(colId);
    },
    onMouseLeave: () => {
      if (rowId !== undefined) table.setHoveredRowId?.(null);
      if (colId !== undefined) table.setHoveredColId?.(null);
    },
  };
}
